import { navigate } from 'gatsby';
import { DateTime } from 'luxon';
import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { DropzoneRef } from 'react-dropzone';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';
import Button from '@ui/atoms/Button';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import TextButton, { IButtonProps } from '@ui/atoms/TextButton';

import Dropzone from '@ui/forms/Dropzone';
import PhotoTaker from '@ui/forms/PhotoTaker';

import aligned from '@ui/styled/Aligned';

import { exists } from '@utils/general';
import { ImageFingerprintError, processImage } from '@utils/image';

import { DropzoneErrors } from '@folx-types/error';

import theme from '@theme/FolxTheme';

const DEFAULT_TEXT = 'Recommended: Square JPG, or PNG, at least 500 pixels per side.';

type ImageTypes = 'Selfie' | 'Photo ID' | 'Labs';

interface IPhotoUploadProps {
	handleUpload: (upload: string, file?: File) => void;
	acceptedTypes?: string[];
	children?: ReactNode;
	className?: string;
	error?: boolean;
	handleError?: (error: DropzoneErrors | '') => void;
	hideDelete?: boolean;
	imageType?: ImageTypes;
	isMobile?: boolean;
	previousUpload?: string;
	previousName?: string;
	variant?: 'column' | 'row';
}

const PhotoWrapper = styled.div<{
	isMobile: boolean;
	variant: 'column' | 'row';
	imageType: ImageTypes;
}>`
	align-items: center;
	display: flex;
	overflow: hidden;
	flex-direction: ${({ variant }) => variant};
	row-gap: ${({ variant }) => variant === 'column' && theme.spacing.sm};
	column-gap: ${({ variant }) => variant === 'row' && theme.spacing.md};

	background: ${({ imageType }) => imageType === 'Photo ID' && theme.colors.white};
	justify-content: ${({ imageType }) => imageType === 'Photo ID' && 'space-between'};
	padding-right: ${({ imageType }) => imageType === 'Photo ID' && theme.spacing.md};
	border: ${({ imageType }) => imageType === 'Photo ID' && `0.1rem solid ${theme.colors.black}`};
	border-radius: ${({ imageType }) => imageType === 'Photo ID' && theme.radius.xl};
`;

const ActionWrapper = styled(FlexColumn)<{
	variant: 'column' | 'row';
	imageType: ImageTypes;
}>`
	align-items: ${({ variant }) => (variant === 'row' ? 'flex-start' : 'center')};
	justify-content: center;
	gap: ${({ imageType }) => imageType !== 'Photo ID' && theme.spacing.sm};
`;

const TextWrapper = styled(ActionWrapper)<{ variant: 'column' | 'row' }>`
	max-width: ${({ variant }) => variant === 'row' && '15rem'};
`;

const Aligned = aligned('span');
const AlignedTextButton = aligned<IButtonProps>(TextButton);

const PhotoUpload: FC<IPhotoUploadProps> = ({
	acceptedTypes,
	children,
	className = '',
	error = false,
	handleUpload,
	handleError,
	imageType = 'Selfie',
	isMobile = false,
	previousUpload,
	previousName,
	hideDelete = false,
	variant = 'column',
}) => {
	const [upload, setUpload] = useState('');
	const [file, setFile] = useState<File | undefined>(undefined);
	const dropzoneRef = useRef<DropzoneRef | null>(null);

	useEffect(() => {
		if (file) {
			handleUpload(upload, file);
		}
	}, [upload, file]);

	useEffect(() => {
		previousUpload && setUpload(previousUpload);
	}, [previousUpload]);

	const handleProcessImageException = (error: Error) => {
		if (error instanceof ImageFingerprintError) {
			navigate('../image_upload_error');
		}
	};

	return (
		<FlexColumn gap="sm" className={className}>
			<PhotoWrapper isMobile={isMobile} variant={variant} imageType={imageType}>
				<Dropzone
					acceptImageHandler={(image, file) => {
						setFile(file);
						processImage(image, file)
							.then((newImage) => {
								setUpload(newImage);
							})
							.catch(handleProcessImageException);
					}}
					acceptedTypes={
						imageType === 'Selfie'
							? ['image/jpeg', 'image/png', 'image/heic', 'image/heif']
							: acceptedTypes
					}
					acceptedImage={upload}
					acceptedName={file?.name ?? previousName}
					cancelImageHandler={() => {
						setUpload('');
						setFile(undefined);
					}}
					error={error}
					errorImageHandler={handleError}
					imageType={imageType}
					ref={dropzoneRef}
				/>
				<ActionWrapper variant={variant} imageType={imageType}>
					{!hideDelete && upload && imageType === 'Selfie' ? (
						<TextButton
							color="black"
							label="Delete"
							variant="text"
							underline
							onClick={() => {
								setUpload('');
								setFile(undefined);
							}}
						/>
					) : (
						imageType === 'Selfie' && (
							<PhotoTaker
								label="Take Photo"
								color="black"
								variant="text"
								underline
								isMobile={isMobile}
								handleTakePhoto={async (image) => {
									setUpload(image);
									const blob: Blob = await (await fetch(image)).blob();
									const newFile = new File(
										[blob],
										`${imageType} - ${DateTime.now().toLocaleString(DateTime.DATETIME_FULL)}`
									);
									setFile(newFile);
								}}
							/>
						)
					)}
					{exists(dropzoneRef?.current) && imageType === 'Selfie' && (
						<TextButton
							color="black"
							label="Choose a Photo"
							variant="text"
							underline
							onClick={() => {
								dropzoneRef?.current?.open();
							}}
						/>
					)}
					{exists(dropzoneRef?.current) && imageType === 'Photo ID' && (
						<Button
							labelSize="xxs"
							label="Upload"
							onClick={() => {
								dropzoneRef?.current?.open();
							}}
						/>
					)}
					{imageType === 'Selfie' && (
						<TextWrapper variant={variant}>
							<BasicText variant="fineprint">{DEFAULT_TEXT}</BasicText>
							{children}
						</TextWrapper>
					)}
				</ActionWrapper>
			</PhotoWrapper>

			{!hideDelete && upload && imageType === 'Photo ID' ? (
				<AlignedTextButton
					alignSelf="center"
					color="black"
					label="Delete"
					variant="text"
					underline
					onClick={() => {
						setUpload('');
						setFile(undefined);
					}}
				/>
			) : (
				imageType === 'Photo ID' && (
					<Aligned alignSelf="center">
						<PhotoTaker
							label="Take Photo"
							color="black"
							variant="text"
							underline
							isMobile={isMobile}
							handleTakePhoto={async (image) => {
								setUpload(image);
								const blob: Blob = await (await fetch(image)).blob();
								const newFile = new File(
									[blob],
									`${imageType} - ${DateTime.now().toLocaleString(DateTime.DATETIME_FULL)}`
								);
								setFile(newFile);
							}}
						/>
					</Aligned>
				)
			)}
		</FlexColumn>
	);
};

export default styled(PhotoUpload)``;
